import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { SchoolMemberType } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolMemberService } from '../../../../services/member.school.service';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { DlgSelectOptionComponent } from '../../../components/dialogs/dlg-select-option/dlg-select-option.component';
import { ContactComponent } from '../../../components/dropdowns/contact/contact.component';

@Component({
  selector: 'app-dlg-add-student',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    ContactComponent,
    BtnActionComponent,
  ],
  templateUrl: './dlg-add-student.component.html',
  styleUrl: './dlg-add-student.component.css',
})
export class DlgAddStudentComponent implements OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DlgSelectOptionComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private dialog: MatDialog = inject(MatDialog);
  toastr: ToastrService = inject(ToastrService);
  memberService: SchoolMemberService = inject(SchoolMemberService);

  pageForm: FormGroup = new FormGroup({});

  store: Store<AppState> = inject(Store<AppState>);

  title: string = 'Add Student';
  description: string = 'Enter the requested details';
  action: string = 'Create';

  group: Group | null | undefined = undefined;
  grades: Grade[] | null | undefined = [];

  working$: Observable<boolean> = of(false);

  exit$: Subject<void> = new Subject<void>();

  constructor() {
    this.pageForm = new FormGroup({
      firstName: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
        Validators.pattern('^[a-zA-Z]*$'),
      ]),
      surname: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
        Validators.pattern('^[A-Za-z]+ *[A-Za-z]+$'),
      ]),
      gradeId: new FormControl('unassigned'),
      contact: new FormControl(),
    });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });

    this.store
      .select(gradeSelector.getAllGrades)
      .pipe(takeUntil(this.exit$))
      .subscribe((grades) => (this.grades = grades));
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  async submit(): Promise<void> {
    if (this.pageForm.invalid) {
      this.onShowAlert({
        title: 'Form Incomplete',
        message: `Form validation failed. Kindly check the form for errors.`,
      } as AppExceptionModel);
      return;
    }

    this.working$ = of(true);

    const grade: Grade | undefined = this.grades?.find(
      (e) => e.id === this.pageForm.get('gradeId')?.value,
    );

    // Step 1: Check if "parentId_firstName@atchurch.app" auth user exists
    // -  Step 2: If no, create a new user with the email using admin SDK function
    //    -  Step 3: Use the userId to create a new student in this group
    // -  Step 4: If yes, use the userId to create a new student in this group
    try {
      const result = await this.memberService.createStudentUser({
        email: `${this.data.parent.memberId}_${this.pageForm
          .get('firstName')
          ?.value?.toLowerCase()}@atchurch.app`,
        name: this.pageForm.get('firstName')?.value,
        surname: this.pageForm.get('surname')?.value,
        unregistered: true,
        groupId: this.data.groupId,
        gradeId: this.pageForm.get('gradeId')?.value,
        gradeName: grade?.gradeName ?? 'unassigned',
        gradeYear: this.group?.academicYear,
        parentId: this.data.parent.memberId,
        parentName: this.data.parent.name,
        parentPhotoUrl: this.data.parent.photoURL,
        contact: this.pageForm.get('contact')?.value,
      });
      this.toastr.success(result.message, 'Success');
      this.working$ = of(false);

      this.store.dispatch(
        memberActions.addMembersToStore({
          members: [
            {
              ...this.data.parent,
              studentId: [...this.data.parent.studentId, result.uid],
              modified: Timestamp.now(),
            },
            {
              id: `${this.data.groupId}_${result.uid}`,
              groupId: this.data.groupId,
              memberId: result.uid,
              memberType: [SchoolMemberType.student],
              name: `${this.pageForm.get('firstName')?.value} ${
                this.pageForm.get('surname')?.value
              }`,
              unregistered: true,
              status: MembershipStatus.active,
              gradeId: this.pageForm.get('gradeId')?.value,
              gradeName: grade?.gradeName ?? 'unassigned',
              gradeYear: this.group?.academicYear,
              parentId: this.data.parent.memberId,
              parentName: this.data.parent.name,
              parentPhotoUrl: this.data.parent.photoURL,
              contact:
                this.pageForm.get('contact')?.value ?? this.data.parent.contact,
              created: Timestamp.now(),
              modified: Timestamp.now(),
            },
          ],
        }),
      );
      this.dialogRef.close(true);
    } catch (error) {
      this.working$ = of(false);
      this.toastr.error('Failed to create student user', 'Error Occurred');
    }
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title,
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
